@if (mode) {
  <div
    #overlayEl
    class="measure-overlay"
    data-testid="preview-measure-overlay"
    [attr.data-mode]="mode === 'calibrate' ? 'ruler' : mode"
    [attr.data-phase]="phase"
    [attr.data-operation]="mode === 'calibrate' ? 'scaleReference' : mode === 'ruler' ? 'measure' : null"
    [attr.inert]="chatReview ? '' : null"
    [attr.data-area-shape]="mode === 'area' ? areaShape : null"
    [class.ruler-mode]="mode === 'ruler'"
    [class.area-mode]="mode === 'area'"
    [class.select-mode]="mode === 'area' && areaShape === 'select'"
    [class.calibrate-mode]="mode === 'calibrate'"
    [class.calibration-line-placed]="mode === 'calibrate' && phase === 'complete'"
    [style.left.px]="canvasOffsetX"
    [style.top.px]="canvasOffsetY"
    [style.width.px]="canvasWidth"
    [style.height.px]="canvasHeight"
    (pointerdown)="onOverlayPointerDown($event)"
    (pointermove)="onOverlayPointerMove($event)"
    (pointerup)="onOverlayPointerUp($event)"
    (pointercancel)="onOverlayPointerUp($event)"
    (dblclick)="onOverlayDoubleClick($event)"
    role="region"
    tabindex="0"
    [attr.aria-label]="overlayLabel"
    [attr.aria-description]="overlayDescription"
  >
    <!-- SVG drawing layer -->
    <svg [attr.width]="canvasWidth" [attr.height]="canvasHeight" class="measure-svg">
      <!-- Ruler mode: open polyline with committed vertex markers -->
      @if (mode === 'ruler' && phase !== 'idle') {
        <polyline [attr.points]="rulerPolylinePoints" class="measure-line" />
        @for (point of rulerVertexPoints; track $index) {
          <circle [attr.cx]="point.x" [attr.cy]="point.y" r="5" class="measure-endpoint" />
        }
        @if (phase === 'anchored' && rulerLiveLength >= 0.5) {
          <circle [attr.cx]="cursorX" [attr.cy]="cursorY" r="4" class="measure-live-endpoint" />
        }
      }

      <!-- Calibration mode: adjustable two-end reference line -->
      @if (mode === 'calibrate' && phase !== 'idle') {
        <line
          [attr.x1]="anchorX"
          [attr.y1]="anchorY"
          [attr.x2]="cursorX"
          [attr.y2]="cursorY"
          class="measure-line"
          [class.calibration-line]="true"
        />
        <!-- Anchor point -->
        <circle [attr.cx]="anchorX" [attr.cy]="anchorY" r="5" class="measure-endpoint" />
        <!-- Cursor / end point -->
        <circle [attr.cx]="cursorX" [attr.cy]="cursorY" r="5" class="measure-endpoint" />
      }

      <!-- Committed Area selections remain visible and individually selectable (Issue #2440). -->
      @if (mode === 'area') {
        <!--
          Overlaps counted once: the joined shape, drawn first so the selected area shows on top of it.
        -->
        @for (path of areaUnionPaths; track $index) {
          <path [attr.d]="path" class="area-union-perimeter" data-testid="preview-area-union-perimeter" />
        }
        @for (object of areaObjectViews; track object.id) {
          @if (object.kind === 'rectangle') {
            <rect
              [attr.x]="object.x"
              [attr.y]="object.y"
              [attr.width]="object.width"
              [attr.height]="object.height"
              class="committed-area-shape"
              [class.in-union]="excludeAreaOverlaps"
              [class.editable]="areaShape === 'select'"
              [class.selected]="object.selected"
              data-testid="preview-area-rectangle"
              [attr.role]="areaShape === 'select' ? 'button' : null"
              [attr.tabindex]="areaShape === 'select' ? 0 : null"
              [attr.aria-label]="
                object.label + (object.selected ? ', selected' : '') + (areaShape === 'select' ? '. Drag to move.' : '')
              "
              (pointerdown)="onAreaObjectPointerDown($event, object.id)"
              (keydown.enter)="selectAreaObject($event, object.id)"
              (keydown.space)="selectAreaObject($event, object.id)"
            />
          } @else if (object.kind === 'ellipse') {
            <ellipse
              [attr.cx]="object.cx"
              [attr.cy]="object.cy"
              [attr.rx]="object.rx"
              [attr.ry]="object.ry"
              class="committed-area-shape"
              [class.in-union]="excludeAreaOverlaps"
              [class.editable]="areaShape === 'select'"
              [class.selected]="object.selected"
              data-testid="preview-area-ellipse"
              [attr.role]="areaShape === 'select' ? 'button' : null"
              [attr.tabindex]="areaShape === 'select' ? 0 : null"
              [attr.aria-label]="
                object.label + (object.selected ? ', selected' : '') + (areaShape === 'select' ? '. Drag to move.' : '')
              "
              (pointerdown)="onAreaObjectPointerDown($event, object.id)"
              (keydown.enter)="selectAreaObject($event, object.id)"
              (keydown.space)="selectAreaObject($event, object.id)"
            />
          } @else {
            <polygon
              [attr.points]="object.points"
              class="committed-area-shape"
              [class.in-union]="excludeAreaOverlaps"
              [class.editable]="areaShape === 'select'"
              [class.selected]="object.selected"
              data-testid="preview-area-polygon"
              [attr.role]="areaShape === 'select' ? 'button' : null"
              [attr.tabindex]="areaShape === 'select' ? 0 : null"
              [attr.aria-label]="
                object.label + (object.selected ? ', selected' : '') + (areaShape === 'select' ? '. Drag to move.' : '')
              "
              (pointerdown)="onAreaObjectPointerDown($event, object.id)"
              (keydown.enter)="selectAreaObject($event, object.id)"
              (keydown.space)="selectAreaObject($event, object.id)"
            />
          }
        }
      }

      <!-- Area mode: shape preview in normalized page coordinates (Issue #2439). -->
      @if (mode === 'area' && phase === 'anchored') {
        @if (areaShape === 'rectangle') {
          <rect
            [attr.x]="rectLeft"
            [attr.y]="rectTop"
            [attr.width]="rectWidth"
            [attr.height]="rectHeight"
            class="measure-shape"
            data-testid="preview-area-rectangle"
          />
          <circle [attr.cx]="rectLeft" [attr.cy]="rectTop" r="4" class="measure-endpoint" />
          <circle [attr.cx]="rectLeft + rectWidth" [attr.cy]="rectTop" r="4" class="measure-endpoint" />
          <circle [attr.cx]="rectLeft" [attr.cy]="rectTop + rectHeight" r="4" class="measure-endpoint" />
          <circle [attr.cx]="rectLeft + rectWidth" [attr.cy]="rectTop + rectHeight" r="4" class="measure-endpoint" />
        } @else if (areaShape === 'ellipse') {
          <ellipse
            [attr.cx]="ellipseCenterX"
            [attr.cy]="ellipseCenterY"
            [attr.rx]="ellipseRadiusX"
            [attr.ry]="ellipseRadiusY"
            class="measure-shape"
            data-testid="preview-area-ellipse"
          />
          <circle [attr.cx]="rectLeft" [attr.cy]="rectTop" r="4" class="measure-endpoint" />
          <circle
            [attr.cx]="rectLeft + rectWidth"
            [attr.cy]="rectTop + rectHeight"
            r="4"
            class="measure-live-endpoint"
          />
        } @else {
          <polyline [attr.points]="polygonPolylinePoints" class="measure-line" data-testid="preview-area-polygon" />
          @if (polygonClosingStart; as closing) {
            @if (polygonFirstPoint; as first) {
              <line
                [attr.x1]="closing.x"
                [attr.y1]="closing.y"
                [attr.x2]="first.x"
                [attr.y2]="first.y"
                class="measure-closing-preview"
              />
            }
          }
          @if (areaShape === 'polygon') {
            @for (point of polygonVertexPoints; track $index) {
              <circle [attr.cx]="point.x" [attr.cy]="point.y" r="5" class="measure-endpoint" />
            }
          }
          @if (phase === 'anchored' && hasNonZeroPolygonLiveSegmentForTemplate) {
            <circle [attr.cx]="cursorX" [attr.cy]="cursorY" r="4" class="measure-live-endpoint" />
          }
        }
      }
    </svg>

    <!--
      Calibration line handles (Issue #2442). Real buttons rather than SVG shapes, so each end can
      take keyboard focus and be dragged with a finger as well as a mouse.
    -->
    @if (mode === 'calibrate' && phase === 'complete') {
      <button
        type="button"
        class="endpoint-handle"
        data-testid="calibration-handle-start"
        aria-label="Start of the reference line. Use the arrow keys to move it; hold Shift for larger steps."
        [style.left.px]="anchorX"
        [style.top.px]="anchorY"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="onHandlePointerDown($event, 'start')"
        (pointermove)="onHandlePointerMove($event, 'start')"
        (pointerup)="onHandlePointerUp($event)"
        (pointercancel)="onHandlePointerUp($event)"
        (keydown)="onHandleKeydown($event, 'start')"
      ></button>
      <button
        type="button"
        class="endpoint-handle"
        data-testid="calibration-handle-end"
        aria-label="End of the reference line. Use the arrow keys to move it; hold Shift for larger steps."
        [style.left.px]="cursorX"
        [style.top.px]="cursorY"
        (mousedown)="$event.stopPropagation()"
        (pointerdown)="onHandlePointerDown($event, 'end')"
        (pointermove)="onHandlePointerMove($event, 'end')"
        (pointerup)="onHandlePointerUp($event)"
        (pointercancel)="onHandlePointerUp($event)"
        (keydown)="onHandleKeydown($event, 'end')"
      ></button>
    }

    <!-- The areas cannot be edited while the chat image of one of them is being reviewed (Issue #2542). -->
    @if (chatReview) {
      <div class="areas-locked" data-testid="preview-areas-locked" role="status">
        <mat-icon aria-hidden="true">lock</mat-icon>
        <span>Areas are locked while the chat image is open</span>
      </div>
    }

    <!--
      The result of a measurement. Calibration shows its own panel instead.

      It is a template so that it can be drawn in one of two places (Issue #2542). As a card it is
      drawn here, on the page. As the phone sheet for areas it must not scroll with the page, so the
      Preview draws this same template in its own container, beside the toolbar; see
      `sheetTemplate`. Either way it is this component's view, with this component's bindings.
    -->
    @if (showResult && !sheetHosted) {
      <ng-container [ngTemplateOutlet]="resultTemplate" />
    }

    <!--
      The live value at the pointer while a shape is being drawn (Issue #2542). One line, so it does
      not hide the drawing; everything else about the measurement is in the docked panel or sheet.
    -->
    @if (chipText; as chip) {
      <div
        class="measure-chip"
        data-testid="preview-measure-chip"
        aria-hidden="true"
        [style.left.px]="cursorX + 14"
        [style.top.px]="cursorY + 14"
      >
        {{ chip }}
      </div>
    }
    <ng-template #resultTemplate>
      <mat-card
        #resultCard
        class="measure-card"
        tabindex="-1"
        role="group"
        [attr.aria-label]="mode === 'ruler' ? 'Ruler result' : 'Area result'"
        [class.recalculated]="justRecalculated"
        [class.area-sheet]="areaSheet"
        [class.docked-panel]="panelLayout"
        [class.on-right]="panelLayout && panelSide === 'right'"
        [class.sheet-compact]="sheetCompact"
        [class.review-hidden]="docked && chatReview"
        data-testid="preview-measure-result"
        [attr.data-layout]="resultLayout"
        [attr.data-side]="panelLayout ? panelSide : null"
        [style.left.px]="docked ? null : cardLeft"
        [style.top.px]="docked ? null : cardTop"
        [style.bottom.px]="docked ? areaSheetBottom : null"
        [style.max-height.px]="areaSheet ? areaSheetMaxHeight : null"
        (pointerdown)="$event.stopPropagation()"
        (pointermove)="$event.stopPropagation()"
      >
        <mat-card-content>
          <!-- Ruler info -->
          @if (mode === 'ruler' && rulerData) {
            <!-- The one figure a reviewer came for; it is what the compact panel and sheet show. -->
            @if (docked) {
              <div class="result-primary keep-compact" data-testid="preview-ruler-primary">
                <span class="measure-label">Total distance</span>
                <strong class="surface-area-value">{{ physicalText || rulerData.pdfInches }}</strong>
              </div>
            }
            <div class="measure-section-title">Total distance</div>
            <div class="measure-rows">
              <div class="measure-row">
                <span class="measure-label">PDF</span>
                <span class="measure-value">{{ rulerData.pdfInches }}</span>
              </div>
              <div class="measure-row">
                <span class="measure-label">Image</span>
                <span class="measure-value">{{ rulerData.imagePx }}</span>
              </div>
              <div class="measure-row">
                <span class="measure-label" title="Length as thousandths of the sheet">Of sheet</span>
                <span class="measure-value">{{ rulerData.perMille }}</span>
              </div>
              <div class="measure-row">
                <span class="measure-label">Physical</span>
                <span class="measure-value" [class.detecting]="scaleDetecting">
                  @if (physicalText) {
                    {{ physicalText }}
                  } @else {
                    <span class="no-scale">No scale detected</span>
                  }
                </span>
              </div>
            </div>
            @if (phase === 'anchored' && rulerLiveData) {
              <div class="measure-row-divider"></div>
              <div class="measure-section-title secondary">Current segment</div>
              <div class="measure-rows live-measurement">
                <div class="measure-row">
                  <span class="measure-label">PDF</span>
                  <span class="measure-value">{{ rulerLiveData.pdfInches }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Image</span>
                  <span class="measure-value">{{ rulerLiveData.imagePx }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label" title="Length as thousandths of the sheet">Of sheet</span>
                  <span class="measure-value">{{ rulerLiveData.perMille }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Physical</span>
                  <span class="measure-value" [class.detecting]="scaleDetecting">
                    @if (livePhysicalText) {
                      {{ livePhysicalText }}
                    } @else {
                      <span class="no-scale">No scale detected</span>
                    }
                  </span>
                </div>
              </div>
            }
          }

          <!-- Shape-based Area info (Issue #2439) -->
          @if (mode === 'area') {
            <div class="area-result-heading">
              <mat-icon aria-hidden="true">dashboard_customize</mat-icon>
              <span>Area measurements</span>
              <span class="area-object-count" data-testid="preview-area-object-count">
                {{ areaObjectCount }} {{ areaObjectCount === 1 ? 'area' : 'areas' }}
              </span>
            </div>

            @if (polygonError) {
              <div class="polygon-error keep-compact" role="alert" data-testid="preview-area-polygon-error">
                <mat-icon aria-hidden="true">error_outline</mat-icon>
                <span>{{ polygonError }}</span>
              </div>
            }

            <div class="surface-area-result keep-compact" data-testid="preview-surface-area">
              <span class="measure-label">
                {{ excludeAreaOverlaps ? 'Covered surface' : 'Cumulative area' }}
                @if (isPreviewingCumulativeArea) {
                  <span class="preview-badge">Preview</span>
                }
              </span>
              @if (cumulativeAreaText) {
                <strong class="surface-area-value" [class.detecting]="scaleDetecting">{{ cumulativeAreaText }}</strong>
              } @else if ((cumulativeSurfaceData || previewCumulativeSurfaceData) && !scaleDetecting) {
                <strong class="surface-area-value no-scale">No scale detected</strong>
              } @else if (areaShape === 'polygon' && phase === 'anchored') {
                <strong class="surface-area-value pending">Finish polygon to calculate</strong>
              }
              <!-- The outline's length, by the same overlap rule as the area above (Issue #2542). -->
              @if (cumulativePerimeterText) {
                <span class="perimeter-line" data-testid="preview-area-perimeter">
                  <span class="measure-label">Perimeter</span>
                  <span class="measure-value">{{ cumulativePerimeterText }}</span>
                </span>
              }
            </div>

            <fieldset class="overlap-mode" data-testid="preview-area-overlap-mode">
              <legend>Overlap calculation</legend>
              <label>
                <input
                  type="radio"
                  name="area-overlap-mode"
                  value="double"
                  [checked]="!excludeAreaOverlaps"
                  (pointerdown)="$event.stopPropagation()"
                  (change)="setAreaOverlapMode(false, $event)"
                />
                <span>Double-count overlap</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="area-overlap-mode"
                  value="once"
                  [checked]="excludeAreaOverlaps"
                  (pointerdown)="$event.stopPropagation()"
                  (change)="setAreaOverlapMode(true, $event)"
                />
                <span>Count overlap once</span>
              </label>
            </fieldset>

            @if (excludeAreaOverlaps && grossSurfaceData) {
              <div class="union-breakdown" data-testid="preview-area-union-breakdown">
                <div class="measure-row">
                  <span class="measure-label">Gross area</span>
                  <span class="measure-value">{{ grossAreaText || 'No scale detected' }}</span>
                </div>
                <div class="measure-row overlap-deduction">
                  <span class="measure-label">Overlap deducted</span>
                  <span class="measure-value">− {{ overlapAreaText || 'No scale detected' }}</span>
                </div>
                <div class="measure-row covered-total">
                  <span class="measure-label">Covered surface</span>
                  <span class="measure-value">{{ coveredAreaText || 'No scale detected' }}</span>
                </div>
              </div>
            }

            @if (selectedAreaObject; as selected) {
              <div class="selected-area-summary" data-testid="preview-selected-area">
                <span>
                  <strong>{{ selectedAreaObjectLabel }}</strong>
                  selected
                </span>
                <span class="measure-value">
                  {{ selectedAreaText || 'No scale detected' }}
                  @if (selectedPerimeterText) {
                    <span class="selected-perimeter" data-testid="preview-selected-perimeter"
                      >· perimeter {{ selectedPerimeterText }}</span
                    >
                  }
                </span>
              </div>
            }

            <div class="area-session-controls" aria-label="Edit area measurements">
              <button
                mat-icon-button
                type="button"
                data-testid="preview-area-delete"
                [disabled]="selectedAreaObjectId === null"
                aria-label="Delete selected area"
                matTooltip="Delete selected area"
                (pointerdown)="$event.stopPropagation()"
                (click)="deleteSelectedArea($event)"
              >
                <mat-icon>delete_outline</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                data-testid="preview-area-undo"
                [disabled]="!canUndoArea"
                aria-label="Undo area change"
                matTooltip="Undo"
                (pointerdown)="$event.stopPropagation()"
                (click)="undoAreaChange($event)"
              >
                <mat-icon>undo</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                data-testid="preview-area-redo"
                [disabled]="!canRedoArea"
                aria-label="Redo area change"
                matTooltip="Redo"
                (pointerdown)="$event.stopPropagation()"
                (click)="redoAreaChange($event)"
              >
                <mat-icon>redo</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                data-testid="preview-area-clear"
                [disabled]="areaObjectCount === 0"
                aria-label="Clear all area measurements"
                matTooltip="Clear all"
                (pointerdown)="$event.stopPropagation()"
                (click)="clearAreaSession($event)"
              >
                <mat-icon>delete_sweep</mat-icon>
              </button>
            </div>

            <!--
              Chat attachment adapter (Issue #2542): one measured area can be sent to chat through the
              review of Issue #1747. A chat attachment is a single outline, so with several areas one
              has to be selected first. Until then the button is disabled but stays focusable
              (disabledInteractive), so its reason can be reached and is read out with it.
            -->
            @if (mode === 'area' && phase !== 'anchored' && areaObjectCount > 0) {
              <div class="scale-reference-action keep-compact" [class.chat-action-inline]="!!chatAreaObject">
                <button
                  mat-stroked-button
                  type="button"
                  data-testid="preview-area-use-in-chat"
                  [disabled]="!chatAreaObject"
                  disabledInteractive
                  [attr.aria-describedby]="chatAreaObject ? null : 'preview-area-chat-hint'"
                  (pointerdown)="$event.stopPropagation()"
                  (click)="onUseAreaInChat($event)"
                >
                  <mat-icon aria-hidden="true">chat</mat-icon>
                  Use in chat…
                </button>
                @if (!chatAreaObject) {
                  <span class="hint" id="preview-area-chat-hint" data-testid="preview-area-chat-hint"
                    >Chat takes one area. Choose Select &amp; move in Tools and click the area to send.</span
                  >
                }
              </div>
            }

            @if (surfaceData) {
              <div class="measure-rows">
                <div class="measure-row">
                  <span class="measure-label">Area (PDF)</span>
                  <span class="measure-value">{{ surfaceData.pdf }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Area (image)</span>
                  <span class="measure-value">{{ surfaceData.image }}</span>
                </div>
              </div>
            }

            @if (areaData) {
              <div class="measure-row-divider"></div>
              <div class="measure-rows">
                <div class="measure-row">
                  <span class="measure-label">Top-left</span>
                  <span class="measure-value">{{ areaData.topLeft.pdfInches }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label"></span>
                  <span class="measure-value secondary">{{ areaData.topLeft.perMille }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Bottom-right</span>
                  <span class="measure-value">{{ areaData.bottomRight.pdfInches }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label"></span>
                  <span class="measure-value secondary">{{ areaData.bottomRight.perMille }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Size (PDF)</span>
                  <span class="measure-value">{{ areaData.sizePdfInches }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Size (image)</span>
                  <span class="measure-value">{{ areaData.sizeImagePx }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Size (‰)</span>
                  <span class="measure-value">{{ areaData.sizePerMille }}</span>
                </div>
                <div class="measure-row">
                  <span class="measure-label">Dimensions</span>
                  <span class="measure-value" [class.detecting]="scaleDetecting">
                    @if (areaPhysicalText) {
                      {{ areaPhysicalText }}
                    } @else {
                      <span class="no-scale">No scale detected</span>
                    }
                  </span>
                </div>
              </div>
            }

            @if (areaShape === 'polygon') {
              <div class="measure-row-divider"></div>
              <div class="measure-row">
                <span class="measure-label">Vertices</span>
                <span class="measure-value">{{ polygonVertexCount }}</span>
              </div>
            }
          }

          <!-- Which scale the physical values use (Issue #2442) -->
          @if (scaleSourceLabel) {
            <div
              class="scale-source"
              data-testid="preview-measure-scale-source"
              [class.manual]="manuallyCalibrated"
              [attr.data-scale-source]="manuallyCalibrated ? 'manual' : 'automatic'"
            >
              @if (manuallyCalibrated) {
                <mat-icon aria-hidden="true">architecture</mat-icon>
              }
              <span>{{ scaleSourceLabel }}</span>
            </div>
          }

          <!-- Unit toggle + hint row -->
          <!--
            Phone sheet (Issue #2542): opens and folds the details. The sheet is compact unless the
            reviewer opens it, so it never covers the drawing by itself.
          -->
          @if (docked && phase !== 'anchored') {
            <button
              mat-icon-button
              type="button"
              class="sheet-toggle keep-compact"
              data-testid="preview-area-sheet-toggle"
              [attr.aria-expanded]="sheetExpanded ? 'true' : 'false'"
              [attr.aria-label]="sheetExpanded ? 'Hide measurement details' : 'Show measurement details'"
              (pointerdown)="$event.stopPropagation()"
              (click)="toggleSheet($event)"
            >
              <mat-icon>{{ sheetExpanded ? 'expand_more' : 'expand_less' }}</mat-icon>
            </button>
          }

          <div class="measure-footer keep-compact">
            <button
              mat-icon-button
              class="unit-toggle"
              (pointerdown)="$event.stopPropagation()"
              (click)="toggleUnitSystem($event)"
              [matTooltip]="useMetric ? 'Switch to imperial' : 'Switch to metric'"
              [attr.aria-label]="
                useMetric ? 'Switch measurement units to imperial' : 'Switch measurement units to metric'
              "
              matTooltipPosition="below"
            >
              <mat-icon>{{ useMetric ? 'straighten' : 'square_foot' }}</mat-icon>
            </button>
            <span class="unit-label">{{ useMetric ? 'Metric' : 'Imperial' }}</span>
            @if (mode === 'ruler' && phase === 'anchored') {
              <span class="hint">Click to add · ⇧ snap</span>
            }
            @if (mode === 'area') {
              <button
                mat-stroked-button
                type="button"
                data-testid="preview-area-add-rectangle"
                (click)="addKeyboardRectangle($event)"
              >
                Add rectangle
              </button>
            }
            @if (mode === 'ruler') {
              <button
                mat-stroked-button
                class="ruler-action"
                [attr.data-testid]="phase === 'anchored' ? 'preview-ruler-done' : 'preview-ruler-new'"
                [disabled]="phase === 'anchored' && !canFinishRuler"
                [attr.aria-label]="
                  phase === 'anchored' ? 'Finish measurement, including the current segment' : 'Start a new measurement'
                "
                (pointerdown)="$event.stopPropagation()"
                (click)="onRulerAction($event)"
              >
                {{ phase === 'anchored' ? 'Done' : 'New measurement' }}
              </button>
            }
            @if (mode === 'area' && areaShape === 'polygon' && phase === 'anchored') {
              <span class="hint">Click to add · ⌫ undo</span>
              <button
                mat-stroked-button
                class="ruler-action"
                data-testid="preview-polygon-done"
                [disabled]="!canFinishPolygon"
                aria-label="Finish and close polygon"
                (pointerdown)="$event.stopPropagation()"
                (click)="onPolygonAction($event)"
              >
                Done
              </button>
            } @else if (mode === 'area' && areaShape === 'freehand' && phase === 'anchored') {
              <span class="hint">Release to finish · Esc cancels</span>
            } @else if (mode === 'area' && phase === 'anchored') {
              <span class="hint">
                Click to finish
                @if (areaShape === 'rectangle') {
                  · ⇧ square
                } @else if (areaShape === 'ellipse') {
                  · ⇧ circle
                }
              </span>
            } @else if (mode === 'area' && areaShape === 'select') {
              <span class="hint">Drag a shape to move it</span>
            } @else if (mode === 'area') {
              <button
                mat-stroked-button
                class="ruler-action"
                data-testid="preview-area-new"
                aria-label="Add another area measurement"
                (pointerdown)="$event.stopPropagation()"
                (click)="startNewAreaMeasurement($event)"
              >
                Add area
              </button>
            }
          </div>

          <!-- Scale-reference adapter (Issue #2542): a finished straight line can calibrate the page. -->
          @if (mode === 'ruler' && phase === 'complete') {
            <div class="scale-reference-action keep-compact">
              <button
                mat-stroked-button
                type="button"
                data-testid="preview-ruler-use-as-reference"
                [disabled]="!canUseAsScaleReference"
                (pointerdown)="$event.stopPropagation()"
                (click)="onUseAsScaleReference($event)"
              >
                <mat-icon aria-hidden="true">architecture</mat-icon>
                Use as scale reference
              </button>
              @if (!canUseAsScaleReference) {
                <span class="hint" data-testid="preview-ruler-reference-hint"
                  >A scale reference must be a single straight segment.</span
                >
              }
            </div>
          }
        </mat-card-content>
      </mat-card>
    </ng-template>

    <!-- Prompt when idle -->
    @if (phase === 'idle' && (mode !== 'area' || areaObjectCount === 0)) {
      <div class="idle-prompt" data-testid="preview-measure-prompt">
        <mat-icon>{{ idlePromptIcon }}</mat-icon>
        <span>{{ idlePromptText }}</span>
        @if (mode === 'area') {
          <button
            mat-stroked-button
            type="button"
            data-testid="preview-area-add-rectangle"
            (pointerdown)="$event.stopPropagation()"
            (click)="addKeyboardRectangle($event)"
          >
            Add rectangle
          </button>
        }
      </div>
    }
  </div>
}
